<template>
  <div class="p-1 space-y-1 overflow-visible">
    <div class="flex items-center justify-between px-4 py-2 border-b border-gray-200">
      <!-- Left: Spacer -->
      <div class="flex-1"></div>

      <!-- Center: Media Controls + Time Display -->
      <div class="flex items-center space-x-4">
        <!-- Media Controls -->
        <div class="flex items-center space-x-2">
          <!-- Previous/Backward -->
          <button
            class="p-1.5 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            @click="emit('backward')"
          >
            <svg class="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
              <path
                d="M8.445 14.832A1 1 0 0010 14v-2.798l5.445 3.63A1 1 0 0017 14V6a1 1 0 00-1.555-.832L10 8.798V6a1 1 0 00-1.555-.832l-6 4a1 1 0 000 1.664l6 4z"
              />
            </svg>
          </button>

          <!-- Play/Pause -->
          <button
            class="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            @click="emit('play-pause')"
            :disabled="!clipEnabled"
          >
            <svg v-if="!isPlaying" class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M8 5v10l8-5-8-5z" />
            </svg>
            <svg v-else class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M6 4h4v12H6V4zm8 0h4v12h-4V4z" />
            </svg>
          </button>

          <!-- Next/Forward -->
          <button
            class="p-1.5 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            @click="emit('forward')"
          >
            <svg class="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
              <path
                d="M4.555 5.168A1 1 0 003 6v8a1 1 0 001.555.832L10 11.202V14a1 1 0 001.555.832l6-4a1 1 0 000-1.664l-6-4A1 1 0 0010 6v2.798l-5.445-3.63z"
              />
            </svg>
          </button>
        </div>

        <!-- Time Display -->
        <div class="text-sm font-medium text-gray-900">
          {{ formatTime(currentTime) }}/{{ formatTime(duration) }}
        </div>
      </div>

      <!-- Right: Zoom Controls with Icons -->
      <div class="flex-1 flex items-center justify-end gap-2">
        <button
          @click="zoomOut"
          class="flex items-center justify-center w-7 h-7 bg-gray-100 hover:bg-gray-200 rounded"
          title="Zoom Out"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
            <line x1="8" y1="11" x2="14" y2="11" />
          </svg>
        </button>
        <!-- <span class="text-sm text-gray-600">{{ Math.round(pixelsPerSecond) }}px/s</span> -->
        <button
          @click="zoomIn"
          class="flex items-center justify-center w-7 h-7 bg-gray-100 hover:bg-gray-200 rounded"
          title="Zoom In"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
            <line x1="8" y1="11" x2="14" y2="11" />
            <line x1="11" y1="8" x2="11" y2="14" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Timeline UI -->
    <div class="border rounded">
      <!-- Scrollable viewport (ruler + tracks stay aligned) -->
      <div class="viewport overflow-x-auto overflow-y-visible" ref="viewportRef">
        <div class="content" :style="{ width: trackWidth + 'px' }">
          <!-- Ruler -->
          <div class="ruler bg-gray-50 h-8 relative flex">
            <!-- Track Labels Space -->
            <div class="w-8 border-r border-gray-200 flex-shrink-0"></div>
            <!-- Ruler Content -->
            <div class="flex-1 relative">
              <div
                v-for="mark in timeMarks"
                :key="mark.time + '-' + (mark.label || '')"
                class="absolute top-0"
                :style="{ left: mark.x + 'px', transform: 'translateX(-50%)' }"
              >
                <!-- tick line -->
                <div
                  :class="['mx-auto', mark.minor ? 'h-2 w-px bg-gray-300' : 'h-3 w-px bg-gray-500']"
                ></div>

                <!-- only show label if defined -->
                <div v-if="mark.label" class="text-[10px] text-gray-600 text-center mt-1">
                  {{ mark.label }}
                </div>
              </div>
            </div>
          </div>
          <!-- 🚀 SUBTITLE TRACK (OPTIONAL - Disabled by default for performance) -->
          <!-- To enable: pass :show-timeline-subtitles="true" to TimelineEditor component -->
          <SubtitleTrack
            v-if="showTimelineSubtitles && subtitles && subtitles.length > 0"
            :subtitles="subtitles"
            :duration="duration"
            :current-time="currentTime"
            :start-time="0"
            :end-time="duration"
            :track-width="trackWidth - 32"
            :pixels-per-second="pps"
            :is-playing="isPlaying"
            @subtitle-click="handleSubtitleClick"
            @time-click="handleSubtitleTimeClick"
          />

          <!-- Video track: color bar (with vertical spacing above/below) -->
          <div class="track-video relative bg-white flex h-20 mt-2 mb-8">
            <!-- Track Label -->
            <div
              class="w-8 bg-gray-50 border-r border-gray-200 flex items-center justify-center flex-shrink-0"
            >
              <!-- Video icon -->
              <svg
                width="23"
                height="15"
                viewBox="0 0 23 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16.5 2.25V12.75C16.5 13.1478 16.342 13.5294 16.0607 13.8107C15.7794 14.092 15.3978 14.25 15 14.25H1.5C1.10218 14.25 0.720644 14.092 0.43934 13.8107C0.158035 13.5294 0 13.1478 0 12.75V2.25C0 1.85218 0.158035 1.47064 0.43934 1.18934C0.720644 0.908035 1.10218 0.75 1.5 0.75H15C15.3978 0.75 15.7794 0.908035 16.0607 1.18934C16.342 1.47064 16.5 1.85218 16.5 2.25ZM21.9375 2.27344C21.8317 2.24755 21.7216 2.24435 21.6145 2.26406C21.5073 2.28378 21.4056 2.32595 21.3159 2.38781L18.1669 4.48688C18.1155 4.52115 18.0734 4.56758 18.0443 4.62204C18.0152 4.6765 18 4.73731 18 4.79906V10.2009C18 10.2627 18.0152 10.3235 18.0443 10.378C18.0734 10.4324 18.1155 10.4789 18.1669 10.5131L21.3337 12.6244C21.4523 12.7034 21.5909 12.7471 21.7333 12.7503C21.8758 12.7534 22.0162 12.7159 22.1381 12.6422C22.2509 12.5705 22.3434 12.471 22.4068 12.3533C22.4702 12.2356 22.5022 12.1036 22.5 11.97V3C22.5001 2.83366 22.4449 2.67202 22.343 2.54049C22.2412 2.40897 22.0986 2.31502 21.9375 2.27344Z"
                  fill="#646464"
                />
              </svg>
            </div>
            <!-- Color Bar Content -->
            <div class="flex-1 relative">
              <!-- color bars container (horizontal flex) -->
              <div class="color-bars flex h-full">
                <div
                  v-for="(seg, idx) in segments"
                  :key="idx"
                  class="color-bar flex-shrink-0 h-full bg-blue-300 border-r border-blue-400"
                  :style="{
                    width: seg.spanSec * pps + 'px',
                  }"
                ></div>
              </div>
            </div>
          </div>

          <!-- Click-to-seek overlay -->
          <div
            class="absolute inset-0 cursor-pointer"
            @click="handleTimelineClick"
            style="z-index: 10"
          ></div>

          <!-- 🚀 ALL SEGMENTS - Display all edited segments on timeline -->
          <div
            v-for="segment in allSegments"
            :key="segment._tempId || `${segment.start_sec}_${segment.end_sec}`"
            class="segment-bar absolute rounded border-2 transition-all duration-200 cursor-pointer hover:opacity-80"
            :class="{
              'bg-green-400 bg-opacity-30 border-green-600': selectedSegment?._tempId !== segment._tempId,
              'bg-purple-500 bg-opacity-20 border-purple-500 shadow-lg': selectedSegment?._tempId === segment._tempId,
            }"
            :style="{
              left: segment.start_sec * pps + 32 + 'px',
              width: (segment.end_sec - segment.start_sec) * pps + 'px',
              top: '40px',
              height: '80px',
            }"
            style="z-index: 20"
            @click.stop="handleSegmentBarClick(segment)"
          >
            <!-- Segment duration label -->
            <div class="absolute bottom-1 left-1/2 -translate-x-1/2 text-xs font-medium px-1 rounded"
                 :class="{
                   'text-green-700 bg-white bg-opacity-80': selectedSegment?._tempId !== segment._tempId,
                   'text-purple-700 bg-white bg-opacity-80': selectedSegment?._tempId === segment._tempId,
                 }">
              {{ Math.round((segment.end_sec - segment.start_sec) * 10) / 10 }}s
            </div>
          </div>

          <!-- Selection box covering only video track (for trimming) -->
          <div
            v-if="selection"
            class="selection-overlay absolute bg-purple-500 bg-opacity-20 border-2 border-purple-500 rounded-lg shadow-lg"
            :style="{
              left: selection.start * pps + 32 + 'px',
              width: (selection.end - selection.start) * pps + 'px',
              top: '40px',
              height: '80px',
            }"
            style="z-index: 30"
          >
            <div
              class="handle left absolute left-0 top-0 h-full w-2 bg-purple-500 cursor-ew-resize rounded-l"
              @mousedown.stop.prevent="startTrim('left', $event)"
            ></div>
            <div
              class="drag-area absolute inset-0 cursor-grab"
              @mousedown.stop.prevent="startDragSelection($event)"
            ></div>
            <div
              class="handle right absolute right-0 top-0 h-full w-2 bg-purple-500 cursor-ew-resize rounded-r"
              @mousedown.stop.prevent="startTrim('right', $event)"
            ></div>
          </div>

          <!-- Playhead (line across tracks) -->
          <div
            class="playhead absolute top-0 bottom-0 w-[2px] bg-purple-600 cursor-ew-resize"
            :style="{ left: playheadTime * pps + 32 + 'px' }"
            style="z-index: 40"
            @mousedown.stop.prevent="startDraggingPlayhead"
          >
            <!-- Draggable Handle Area as visible inverted triangle (always visible) -->
            <div
              class="absolute top-0 w-0 h-0 z-50 cursor-ew-resize border-l-transparent border-r-transparent border-t-purple-600"
              title="Drag playhead"
              style="
                left: -7px;
                border-left-width: 8px;
                border-right-width: 8px;
                border-top-width: 14px;
              "
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import SubtitleTrack from './subtitle-track.vue'
import type { SRTSubtitle } from '@/services/srt-parser'

// Props interface
interface Props {
  duration: number
  currentTime: number
  isPlaying?: boolean
  selectedSegment?: {
    start_sec: number
    end_sec: number
    _tempId?: string
  } | null
  allSegments?: Array<{
    start_sec: number
    end_sec: number
    _tempId?: string
  }>
  clipEnabled?: boolean
  selectionStart?: number
  selectionEnd?: number
  subtitles?: SRTSubtitle[]
  showTimelineSubtitles?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  duration: 0,
  currentTime: 0,
  isPlaying: false,
  selectedSegment: null,
  allSegments: () => [],
  clipEnabled: true,
  selectionStart: 0,
  selectionEnd: 30,
  subtitles: () => [],
  showTimelineSubtitles: false,
})

// Emits
const emit = defineEmits<{
  'segment-trimmed': [{ startTime: number; endTime: number }]
  'playhead-moved': [{ currentTime: number }]
  'playhead-drag-end': [{ currentTime: number }]
  'play-pause': []
  backward: []
  forward: []
  'trim-start': []
  'trim-end': []
  'subtitle-click': [subtitle: SRTSubtitle]
  'subtitle-time-click': [time: number]
  'segment-bar-click': [segment: { start_sec: number; end_sec: number; _tempId?: string }]
}>()

/* -------------------------
  Config / Reactive state
   ------------------------- */
const viewportRef = ref<HTMLElement | null>(null)

const playheadTime = ref(props.currentTime) // seconds

// Start with optimal zoom for subtitle visibility (1 second level)
// Calculate pixels per second to show ~10-15 seconds in viewport for good subtitle visibility
const getOptimalZoom = (duration: number) => {
  if (duration <= 0) return 20 // Default fallback

  // For subtitle visibility, aim for 10-15 seconds visible in viewport
  // Assuming viewport width is ~1200px (accounting for track labels)
  const viewportWidth = 1200 - 32 // Subtract track label width
  const targetSecondsVisible = Math.min(15, Math.max(10, duration / 20)) // 10-15 seconds or 1/20th of duration
  return Math.max(10, Math.min(50, viewportWidth / targetSecondsVisible))
}

const pixelsPerSecond = ref(getOptimalZoom(props.duration))
const pps = computed(() => pixelsPerSecond.value)

const LABEL_OFFSET = 32
const hasAutoSelectedFirst = ref(false)

// Viewport metrics for virtualized rendering and smoother zooming
const viewLeftPx = ref(0)
const viewportWidthPx = ref(0)

// rAF-batched zooming
let zoomAccum = 1
let zoomRaf: number | null = null
let skipScrollOnZoom = false

// Throttle auto-scroll to prevent excessive scrolling
let autoScrollThrottle: NodeJS.Timeout | null = null

const thumbnailIntervalSec = computed(() => {
  // Adjust thumbnail interval based on duration
  if (props.duration > 7200) return 60 // 1 minute for very long videos
  if (props.duration > 3600) return 30 // 30 seconds for long videos
  if (props.duration > 1800) return 10 // 10 seconds for medium videos
  return 5 // 5 seconds for short videos
})

/* selection only on video track - synced with parent */
const selection = reactive({
  start: props.selectedSegment?.start_sec || props.selectionStart || 0,
  end: props.selectedSegment?.end_sec || props.selectionEnd || Math.min(30, props.duration),
})

/* internal flag for drag actions */
let dragState = null as any | null

/* -------------------------
  Derived sizes & ruler
   ------------------------- */
const trackWidth = computed(() => Math.ceil(props.duration * pps.value) + 32) // Add 32px for track labels

function scrollRangeIntoView(
  startSec: number,
  endSec: number,
  opts?: {
    paddingPx?: number // how much cushion to keep at edges
    behavior?: ScrollBehavior // 'auto' | 'smooth'
  },
) {
  const vp = viewportRef.value
  if (!vp) return

  const padding = opts?.paddingPx ?? 24
  const behavior = opts?.behavior ?? 'smooth'

  // Convert selection start/end to content pixels (include gutter)
  const startPx = startSec * pps.value + LABEL_OFFSET
  const endPx = endSec * pps.value + LABEL_OFFSET

  const viewLeft = vp.scrollLeft
  const viewRight = vp.scrollLeft + vp.clientWidth

  // If fully inside viewport already → nothing to do
  if (startPx >= viewLeft + padding && endPx <= viewRight - padding) return

  // If selection is left of viewport → scroll left edge into view with padding
  if (startPx < viewLeft + padding) {
    const target = Math.max(0, startPx - padding)
    vp.scrollTo({ left: target, behavior })
    return
  }

  // If selection is right of viewport → scroll so right edge is visible with padding
  if (endPx > viewRight - padding) {
    const target = Math.max(0, endPx - vp.clientWidth + padding)
    vp.scrollTo({ left: target, behavior })
  }
}

function getTickInterval(pps: number): number {
  // For very long videos, we need more appropriate intervals
  if (pps >= 100) return 0.25 // show quarter-second
  if (pps >= 60) return 0.5 // half-second ticks
  if (pps >= 25) return 1 // every 1s
  if (pps >= 10) return 5 // 5 seconds
  if (pps >= 5) return 10 // 10 seconds
  if (pps >= 3) return 30 // 30 seconds
  if (pps >= 1) return 60 // 1 min
  if (pps >= 0.5) return 300 // 5 minutes
  if (pps >= 0.2) return 600 // 10 minutes
  if (pps >= 0.1) return 900 // 15 minutes
  return 1800 // 30 minutes for very zoomed out
}

const timeMarks = computed(() => {
  const marks: { time: number; x: number; label?: string; minor?: boolean }[] = []
  const p = pps.value
  const step = getTickInterval(p)

  // Determine visible time window based on viewport scroll and width (with small buffer)
  const bufferPx = 200
  const leftPx = Math.max(0, viewLeftPx.value - bufferPx)
  const rightPx = viewLeftPx.value + (viewportWidthPx.value || 1200) + bufferPx
  const startSecVisible = Math.max(0, (leftPx - LABEL_OFFSET) / p)
  const endSecVisible = Math.min(props.duration, (rightPx - LABEL_OFFSET) / p)

  // How often should we show labels (not every tick)?
  let labelEvery = step
  if (step < 1) labelEvery = 1
  else if (step <= 5) labelEvery = 5
  else if (step <= 10) labelEvery = 10
  else if (step <= 30) labelEvery = 30
  else if (step <= 60) labelEvery = 60
  else if (step <= 300) labelEvery = 300
  else if (step <= 600) labelEvery = 600
  else if (step <= 900) labelEvery = 900
  else labelEvery = 1800

  // Align start to nearest tick boundary for stable labels
  const startAligned = Math.max(0, Math.floor(startSecVisible / step) * step)

  for (let t = startAligned; t <= endSecVisible; t += step) {
    const isLabel = t % labelEvery === 0

    marks.push({
      time: t,
      x: t * p,
      label: isLabel ? formatTime(t) : undefined,
      minor: !isLabel,
    })

    // Subdivisions (like half/quarter ticks)
    if (step >= 1) {
      const half = t + step / 2
      if (half <= endSecVisible) marks.push({ time: half, x: half * p, minor: true })
    }

    if (step <= 1) {
      const q1 = t + step / 4
      const q3 = t + (3 * step) / 4
      if (q1 <= endSecVisible) marks.push({ time: q1, x: q1 * p, minor: true })
      if (q3 <= endSecVisible) marks.push({ time: q3, x: q3 * p, minor: true })
    }
  }

  return marks
})

/* -------------------------
  Computed segments for thumbnails
   ------------------------- */
const segments = computed(() => {
  const d = props.duration
  if (!d) return []
  const interval = thumbnailIntervalSec.value
  const out: { start: number; spanSec: number }[] = []
  for (let t = 0; t < d; t += interval) {
    const next = Math.min(t + interval, d)
    out.push({ start: t, spanSec: next - t })
  }
  return out
})


/* -------------------------
  Watchers to sync with parent
   ------------------------- */
watch(
  () => props.currentTime,
  (newTime) => {
    playheadTime.value = newTime
  },
)

// Auto-scroll when playhead moves beyond viewport
watch(
  () => playheadTime.value,
  (newTime) => {
    if (props.isPlaying) {
      // Throttle auto-scroll to prevent excessive scrolling
      if (autoScrollThrottle) {
        clearTimeout(autoScrollThrottle)
      }
      autoScrollThrottle = setTimeout(() => {
        autoScrollToPlayhead(newTime)
      }, 100) // 100ms throttle
    }
  },
)

watch(
  () => props.selectedSegment,
  (newSegment) => {
    if (newSegment) {
      selection.start = newSegment.start_sec
      selection.end = newSegment.end_sec
    } else {
      // 🚀 FIX: Hide selection box when segment is deselected
      // Set to invalid range so v-if="selectedSegment" can hide it
      selection.start = 0
      selection.end = 0
    }
  },
)

// Watch for selection changes from parent (clipSelector updates)
watch(
  () => [props.selectionStart, props.selectionEnd],
  ([newStart, newEnd]) => {
    // Only update if no segment is selected (to avoid conflicts)
    if (!props.selectedSegment && newStart !== undefined && newEnd !== undefined) {
      selection.start = newStart
      selection.end = newEnd
    }
  },
)

// Update zoom level when duration changes (for component reuse)
watch(
  () => props.duration,
  (newDuration) => {
    if (newDuration > 0) {
      pixelsPerSecond.value = getOptimalZoom(newDuration)
    }
  },
)


watch(
  () => props.selectedSegment,
  async (seg) => {
    if (seg) {
      selection.start = seg.start_sec
      selection.end = seg.end_sec
      await nextTick()
      scrollRangeIntoView(selection.start, selection.end, { behavior: 'auto' }) // Instant jump
    }
  },
)

watch(
  () => [props.selectionStart, props.selectionEnd],
  async ([newStart, newEnd]) => {
    if (!props.selectedSegment && newStart !== undefined && newEnd !== undefined) {
      selection.start = newStart
      selection.end = newEnd
      await nextTick()
      scrollRangeIntoView(selection.start, selection.end, { behavior: 'auto' }) // Instant jump
    }
  },
)

watch(pps, async () => {
  if (skipScrollOnZoom) return
  await nextTick()
  scrollRangeIntoView(selection.start, selection.end, { behavior: 'auto' })
})

watch(
  segments,
  (list) => {
    if (!hasAutoSelectedFirst.value && !props.selectedSegment && list && list.length) {
      const first = list[0]
      selectSegment(first.start, first.start + first.spanSec, 'smooth')
      hasAutoSelectedFirst.value = true
    }
  },
  { immediate: false },
)

/* -------------------------
  Selection & dragging logic
   ------------------------- */

function scrollTimeIntoCenter(timeSec: number, behavior: ScrollBehavior = 'smooth') {
  const vp = viewportRef.value
  if (!vp) return
  const x = timeSec * pps.value + LABEL_OFFSET
  const target = Math.max(0, x - vp.clientWidth / 2)
  vp.scrollTo({ left: target, behavior })
}

// 🚀 NEW: Instant jump without any animation (direct scrollLeft manipulation)
function jumpToTime(timeSec: number) {
  const vp = viewportRef.value
  if (!vp) return
  const x = timeSec * pps.value + LABEL_OFFSET
  const target = Math.max(0, x - vp.clientWidth / 2)
  // Direct assignment = zero animation, true instant jump
  vp.scrollLeft = target
}

function autoScrollToPlayhead(timeSec: number) {
  const vp = viewportRef.value
  if (!vp) return

  const playheadX = timeSec * pps.value + LABEL_OFFSET
  const viewportLeft = vp.scrollLeft
  const viewportRight = viewportLeft + vp.clientWidth

  // Define margins for when to start auto-scrolling (20% from edges)
  const leftMargin = vp.clientWidth * 0.2
  const rightMargin = vp.clientWidth * 0.2

  const shouldScrollLeft = playheadX < viewportLeft + leftMargin
  const shouldScrollRight = playheadX > viewportRight - rightMargin

  if (shouldScrollLeft || shouldScrollRight) {
    // Calculate optimal scroll position to keep playhead visible
    // Keep playhead in the center-right area of viewport for better visibility
    const targetScrollLeft = Math.max(0, playheadX - vp.clientWidth * 0.7)
    vp.scrollTo({ left: targetScrollLeft, behavior: 'smooth' })
  }
}

function startTrim(side: 'left' | 'right', ev: MouseEvent) {
  const startX = ev.clientX
  const startStart = selection.start
  const startEnd = selection.end
  dragState = { type: 'trim', side, startX, startStart, startEnd }
  emit('trim-start')
  window.addEventListener('mousemove', onDragMove)
  window.addEventListener('mouseup', onDragEnd)
}

function startDragSelection(ev: MouseEvent) {
  const startX = ev.clientX
  const startStart = selection.start
  const startEnd = selection.end
  dragState = { type: 'move', startX, startStart, startEnd }
  window.addEventListener('mousemove', onDragMove)
  window.addEventListener('mouseup', onDragEnd)
}

function onDragMove(ev: MouseEvent) {
  if (!dragState) return
  const dxPx = ev.clientX - dragState.startX
  const dxSec = dxPx / pps.value
  if (dragState.type === 'trim') {
    if (dragState.side === 'left') {
      selection.start = Math.max(0, Math.min(dragState.startStart + dxSec, selection.end - 0.1))
    } else {
      selection.end = Math.min(
        props.duration,
        Math.max(dragState.startEnd + dxSec, selection.start + 0.1),
      )
    }
    // Emit trimming event to parent
    emit('segment-trimmed', { startTime: selection.start, endTime: selection.end })
    scrollRangeIntoView(selection.start, selection.end, { behavior: 'auto' })
  } else if (dragState.type === 'move') {
    // move whole selection
    let newStart = dragState.startStart + dxSec
    let newEnd = dragState.startEnd + dxSec
    // clamp
    if (newStart < 0) {
      newEnd += -newStart
      newStart = 0
    }
    if (newEnd > props.duration) {
      newStart -= newEnd - props.duration
      newEnd = props.duration
    }
    selection.start = newStart
    selection.end = newEnd
    // Emit trimming event to parent
    emit('segment-trimmed', { startTime: selection.start, endTime: selection.end })
  }
}

function onDragEnd() {
  window.removeEventListener('mousemove', onDragMove)
  window.removeEventListener('mouseup', onDragEnd)
  if (dragState?.type === 'trim') emit('trim-end')
  dragState = null
}

/* -------------------------
  Playhead dragging & clicking
   ------------------------- */
function startDraggingPlayhead(e: MouseEvent) {
  dragState = { type: 'playhead' }
  window.addEventListener('mousemove', onPlayheadMove)
  window.addEventListener('mouseup', stopPlayheadDrag)
}

function onPlayheadMove(e: MouseEvent) {
  if (dragState?.type !== 'playhead') return

  const vp = viewportRef.value
  if (!vp) return

  const content = vp.querySelector('.content') as HTMLElement
  if (!content) return

  const contentRect = content.getBoundingClientRect()
  const vpRect = vp.getBoundingClientRect()

  // Calculate mouse position relative to content, accounting for scroll
  const mouseXInViewport = e.clientX - vpRect.left
  const scrollLeft = vp.scrollLeft
  const xInContent = mouseXInViewport + scrollLeft
  // Subtract left label gutter to align math with visible timeline
  const xAfterGutter = Math.max(0, xInContent - LABEL_OFFSET)

  // Convert pixel position to time
  const newTime = Math.max(0, Math.min(props.duration, xAfterGutter / pps.value))

  playheadTime.value = newTime
  // Emit playhead movement to parent
  emit('playhead-moved', { currentTime: newTime })
}

function stopPlayheadDrag() {
  window.removeEventListener('mousemove', onPlayheadMove)
  window.removeEventListener('mouseup', stopPlayheadDrag)
  // notify parent that playhead drag ended at current time
  emit('playhead-drag-end', { currentTime: playheadTime.value })
  dragState = null
}

// Click-to-seek functionality
function handleTimelineClick(e: MouseEvent) {
  // Don't handle clicks if we're already dragging
  if (dragState) return

  const vp = viewportRef.value
  if (!vp) return

  const vpRect = vp.getBoundingClientRect()
  const scrollLeft = vp.scrollLeft

  // Calculate click position relative to content
  const mouseXInViewport = e.clientX - vpRect.left
  const xInContent = mouseXInViewport + scrollLeft
  const xAfterGutter = Math.max(0, xInContent - LABEL_OFFSET)

  // Convert to time
  const newTime = Math.max(0, Math.min(props.duration, xAfterGutter / pps.value))

  playheadTime.value = newTime
  emit('playhead-moved', { currentTime: newTime })
  scrollTimeIntoCenter(newTime, 'auto') // Instant jump instead of smooth scroll
}

// Subtitle event handlers
function handleSubtitleClick(subtitle: SRTSubtitle) {
  console.log('Subtitle clicked:', subtitle)

  // Move playhead to the start of the subtitle
  playheadTime.value = subtitle.startTime
  emit('playhead-moved', { currentTime: subtitle.startTime })

  // Scroll to center the subtitle in viewport
  scrollTimeIntoCenter(subtitle.startTime)

  // Emit the subtitle click event for any additional handling
  emit('subtitle-click', subtitle)
}

function handleSubtitleTimeClick(time: number) {
  console.log('Subtitle time clicked:', time)
  playheadTime.value = time
  emit('playhead-moved', { currentTime: time })
  emit('subtitle-time-click', time)
  scrollTimeIntoCenter(time)
}

// 🚀 NEW: Handle segment bar click on timeline (reverse selection)
function handleSegmentBarClick(segment: { start_sec: number; end_sec: number; _tempId?: string }) {
  // Emit event to parent for reverse selection (timeline → segments list & transcript)
  emit('segment-bar-click', segment)
}

/* -------------------------
  Zoom helpers
   ------------------------- */
function scheduleZoom(multiplier: number) {
  zoomAccum *= multiplier
  if (zoomRaf) return
  zoomRaf = requestAnimationFrame(async () => {
    const prev = pps.value
    skipScrollOnZoom = true
    const next = Math.max(0.5, Math.min(200, pixelsPerSecond.value * zoomAccum))
    pixelsPerSecond.value = next
    keepPlayheadCentered(prev)
    zoomAccum = 1
    zoomRaf = null
    await nextTick()
    skipScrollOnZoom = false
  })
}
// 🚀 IMPROVED: Reduced zoom sensitivity from 1.25 (25%) to 1.1 (10%) for better control
function zoomIn() {
  scheduleZoom(1.1)  // Changed from 1.25 to 1.1 (10% zoom in)
}
function zoomOut() {
  scheduleZoom(1 / 1.1)  // Changed from 1/1.25 to 1/1.1 (10% zoom out)
}
function keepPlayheadCentered(prevPps: number) {
  const vp = viewportRef.value
  if (!vp) return
  const oldX = playheadTime.value * prevPps
  const newX = playheadTime.value * pps.value
  vp.scrollLeft += newX - oldX
}

function selectSegment(startSec: number, endSec: number, behavior: ScrollBehavior = 'smooth') {
  selection.start = startSec
  selection.end = endSec
  // notify parent (optional—comment if you don’t want this here)
  emit('segment-trimmed', { startTime: selection.start, endTime: selection.end })
  // scroll after DOM updates so the box has its final width/left
  nextTick(() => scrollRangeIntoView(selection.start, selection.end, { behavior }))
}

/* -------------------------
  Utilities
   ------------------------- */
function formatTime(sec: number) {
  sec = Math.max(0, Math.floor(sec))
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = Math.floor(sec % 60)
  return h > 0
    ? `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

onMounted(async () => {
  // Initialize first selection only if parent didn't set one
  if (
    !(
      props.selectedSegment ||
      (props.selectionStart !== undefined && props.selectionEnd !== undefined)
    )
  ) {
    // wait until we know duration & segments
    await nextTick()
    const first = segments.value?.[0]
    if (first && !hasAutoSelectedFirst.value) {
      hasAutoSelectedFirst.value = true
      selectSegment(first.start, first.start + first.spanSec, 'smooth')
    }
  }

  // Global keyboard listener for spacebar play/pause (works anywhere in viewport)
  document.addEventListener('keydown', handleKeydown)

  // Trackpad pinch zoom on the timeline viewport
  const viewport = viewportRef.value
  if (viewport) {
    viewport.addEventListener('wheel', handleWheel, { passive: false })
    // Initialize and track viewport metrics
    updateViewportMetrics()
    viewport.addEventListener('scroll', updateViewportMetrics, { passive: true })
    window.addEventListener('resize', updateViewportMetrics)
  }
})

// Keyboard: Space toggles play/pause; ArrowLeft/ArrowRight navigate
const handleKeydown = (event: KeyboardEvent) => {
  if (isInputFocused()) return
  if (event.code === 'Space') {
    event.preventDefault()
    emit('play-pause')
  } else if (event.code === 'ArrowLeft') {
    event.preventDefault()
    emit('backward')
  } else if (event.code === 'ArrowRight') {
    event.preventDefault()
    emit('forward')
  }
}

const isInputFocused = (): boolean => {
  const el = document.activeElement as HTMLElement | null
  if (!el) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || el.getAttribute('contenteditable') === 'true'
}

// 🚀 IMPROVED: Trackpad pinch zoom + reduced scroll sensitivity
const handleWheel = (event: WheelEvent) => {
  const vp = viewportRef.value
  if (!vp) return

  // Most browsers flag pinch with ctrlKey=true; prevent page zoom and map to timeline zoom
  if (event.ctrlKey) {
    event.preventDefault()
    if (event.deltaY < 0) {
      zoomIn()
    } else if (event.deltaY > 0) {
      zoomOut()
    }
  } else {
    // 🚀 NEW: Reduce scroll sensitivity by 60% (multiply by 0.4 instead of 1.0)
    // This makes trackpad scrolling much smoother and more controllable
    event.preventDefault()

    // Support both horizontal (deltaX) and vertical (deltaY) scrolling
    const scrollAmount = (event.deltaX || event.deltaY) * 0.4

    vp.scrollLeft += scrollAmount
  }
}

function updateViewportMetrics() {
  const vp = viewportRef.value
  if (!vp) return
  viewLeftPx.value = vp.scrollLeft
  viewportWidthPx.value = vp.clientWidth
}

/* cleanup */
onBeforeUnmount(() => {
  // Clean up any active drag listeners
  if (dragState) {
    window.removeEventListener('mousemove', onDragMove)
    window.removeEventListener('mouseup', onDragEnd)
    window.removeEventListener('mousemove', onPlayheadMove)
    window.removeEventListener('mouseup', stopPlayheadDrag)
  }
  // Clean up auto-scroll throttle
  if (autoScrollThrottle) {
    clearTimeout(autoScrollThrottle)
  }
  // Clean up global keyboard and wheel listeners
  document.removeEventListener('keydown', handleKeydown)
  const viewport = viewportRef.value
  if (viewport) {
    viewport.removeEventListener('wheel', handleWheel)
    viewport.removeEventListener('scroll', updateViewportMetrics)
  }
  window.removeEventListener('resize', updateViewportMetrics)
})

// 🚀 Expose methods for parent component to call
defineExpose({
  scrollTimeIntoCenter,
  jumpToTime, // Instant jump without animation
})
</script>

<style scoped>
.ruler {
  position: relative;
  min-height: 32px;
}
.viewport {

  height: 32px /* ruler */ + 80px /* video */ + 8px /* top margin */ + 32px /* bottom margin */;
  height: 152px; /* ✅ FIXED: Increased from 128px to 152px to prevent bottom cropping */
  background: white;
  position: relative;
  overflow-x: auto;
  overflow-y: hidden; /* prevent vertical scrolling */
  scrollbar-width: thin;
  scrollbar-color: #d1d5db white;
}

.viewport::-webkit-scrollbar {
  height: 8px;
}

.viewport::-webkit-scrollbar-track {
  background: white;
}

.viewport::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 4px;
}

.viewport::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
.content {
  position: relative;
  height: 152px; /* match viewport */
}

.track-video,
.track-audio {
  height: 80px; /* equal heights */
  position: relative;
}
.thumbnails {
  height: 100%;
}
.thumb-item {
  height: 100%;
  border-right: 1px solid rgba(0, 0, 0, 0.06);
  background-color: #f0f0f0;
}
.selection {
  pointer-events: auto;
  box-sizing: border-box;
}

.playhead {
  z-index: 50;
  pointer-events: auto;
}
.playhead:hover {
  background-color: #7c3aed; /* purple-600 for better visibility on hover */
}
.handle {
  z-index: 45;
}
.selection-overlay {
  pointer-events: auto;
  box-sizing: border-box;
}
.selection-overlay .handle {
  z-index: 35;
}
</style>
